// @see https://github.com/ngx-translate/core

import { ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AnyExtensions as CommonAnyExtensions,
  EventArgs,
  ILocalizationManager,
  LocalizationManager,
  NotSupportedException,
  ObjectExtensions
} from '@mosaik/core';
import { AnyExtensions } from '../../../../Extensions/AnyExtensions';

/**
 * @public
 */
@Directive({
  selector: `[uiLocalize]`
})
export class LocalizeDirective implements OnDestroy, OnInit {
  private readonly _localizationManager: ILocalizationManager;
  private readonly _element: ElementRef;
  private readonly _changeDetectorRef: ChangeDetectorRef;
  private _key: string;
  private _raw: boolean;
  private _defaultValue: string;
  private _delayed: boolean;
  private _intervalId?: ReturnType<typeof setInterval>;
  private _selectorConfig?: { selector: string; isGlobal?: boolean };
  private _values: Array<any>;

  public constructor(localizationManager: LocalizationManager, element: ElementRef, changeDetectorRef: ChangeDetectorRef) {
    this._localizationManager = localizationManager;
    this._element = element;
    this._changeDetectorRef = changeDetectorRef;
    this._key = '';
    this._raw = false;
    this._defaultValue = '';
    this._values = new Array<string>();
    this._delayed = false;
  }

  /**
   * todo Document this
   */
  @Input(`uiLocalizeDefaultValue`)
  public set defaultValue(value: string) {
    this._defaultValue = value;
  }

  /**
   * todo Document this
   */
  @Input(`uiLocalizeRaw`)
  public set raw(value: boolean) {
    this._raw = AnyExtensions.coerceBoolean(value);
  }

  /**
   * todo Document this
   */
  @Input(`uiLocalize`)
  public set key(value: string) {
    if (value) {
      this._key = value;
      this.invalidate();
    }
  }

  /**
   * todo Document this
   */
  @Input(`uiLocalizeArgs`)
  public set args(value: Array<any>) {
    if (!ObjectExtensions.equals(this._values, value)) {
      this._values = value;
      this.invalidate();
    }
  }

  /**
   * @bug https://github.com/angular/angular/issues/15041
   * should be removed when fixed
   */
  @Input(`uiLocalizeSelector`)
  public set selector(value: { selector: string; isGlobal?: boolean }) {
    this._selectorConfig = value;
  }

  /**
   * @bug https://github.com/angular/angular/issues/15041
   * should be removed when fixed
   */
  @Input(`uiLocalizeDelayed`)
  public set delayed(value: boolean) {
    this._delayed = AnyExtensions.coerceBoolean(value);
  }

  public ngOnInit(): void {
    this._localizationManager.cultureInfoProvider.cultureChanged.add(this.onCultureChanged, this);
  }

  public ngOnDestroy(): void {
    this._localizationManager.cultureInfoProvider.cultureChanged.remove(this.onCultureChanged, this);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onCultureChanged(sender: object, args: EventArgs): void {
    this.invalidate();
  }

  private invalidate(): void {
    if (this._raw) {
      if (CommonAnyExtensions.isNotUndefined(this._element.nativeElement.textContent)) {
        this._element.nativeElement.textContent = this._key;
      } else {
        this._element.nativeElement.data = this._key;
      }
    } else {
      if (this._selectorConfig) {
        if (this._delayed) {
          this._intervalId = setInterval(() => {
            const element = this._selectorConfig!.isGlobal
              ? document.querySelector(this._selectorConfig!.selector)
              : this._element.nativeElement.querySelector(this._selectorConfig!.selector);
            if (element) {
              clearInterval(this._intervalId!);
              element.textContent = this._key;
              this.replaceNodeValues(element.childNodes);
            }
          }, 50);
        } else {
          const element = this._selectorConfig.isGlobal
            ? document.querySelector(this._selectorConfig.selector)
            : this._element.nativeElement.querySelector(this._selectorConfig.selector);
          if (element) {
            element.textContent = this._key;
            this.replaceNodeValues(element.childNodes);
          }
        }
      } else {
        if (this._delayed) {
          throw new NotSupportedException('Delayed localization is only supported if a `selector` is provided');
        }
        let nodes = this._element.nativeElement.childNodes as NodeList;
        if (!nodes.length) {
          if (CommonAnyExtensions.isNotUndefined(this._element.nativeElement.textContent)) {
            this._element.nativeElement.textContent = this._key;
          } else {
            this._element.nativeElement.data = this._key;
          }
          nodes = this._element.nativeElement.childNodes as NodeList;
        }
        this.replaceNodeValues(nodes);
      }
    }
  }

  private replaceNodeValues(nodes: NodeList): void {
    Array.from(nodes)
      .filter((node: Node) => node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE)
      .forEach((node: Node) => {
        if (this._key) {
          this._localizationManager
            .getLocalizationAsync(this._key, this._defaultValue, this._values)
            .then(value => {
              if (CommonAnyExtensions.isNotUndefined(node.textContent)) {
                node.textContent = value;
              } else {
                (node as any).data = value;
              }
              this._changeDetectorRef.markForCheck();
            })
            .catch(reason => {
              if (CommonAnyExtensions.isNotUndefined(node.textContent)) {
                node.textContent = reason;
              } else {
                (node as any).data = reason;
              }
            });
        }
      });
  }
}
